import { Typography, Box, Divider, useTheme, useMediaQuery, Stack } from '@mui/material';
import useInspection from 'hooks/useInspection';
import { useProject } from 'hooks/useProject';
import useWidth from 'hooks/useWidth';
import { ToggleValue, ICmnInspectionProps } from 'interface/inspection/InspectionInfo';
import { IInspectionIpsativesProps } from 'interface/project/projectInfo';
import { handleClassificationTitle } from 'libs/mindUtils';
import { useMemo, useState, useEffect, Fragment } from 'react';

interface DocumentCardProps {
  value: ToggleValue;
  obj: IInspectionIpsativesProps;
  total: number;
}

const buttonInfoList = [
  {
    id: 'NN',
    color: '#ff0000',
    text: '전혀 동의하지\n않음',
  },
  {
    id: 'N',
    color: '#ffa500',
    text: '동의하지 않음',
  },
  {
    id: 'A',
    color: '#00ff00',
    text: '동의함',
  },
  {
    id: 'AA',
    color: '#0088ff',
    text: '전적으로 동의함',
  },
];
function DocumentCard3({ value, obj, total }: DocumentCardProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const _width = useWidth();
  const [check, setCheck] = useState(value);
  const {
    detailProgressInfo,
    classification,
    objectKeyIndex,
    changeDetailProgressInfo,
    inspectionSecondClassification,
    inspectionThirdClassification,
    updateInspectionThirdClassificationItem,
  } = useInspection();

  const { projectName } = useProject();

  const nextCondition: Partial<ToggleValue>[] = useMemo(() => ['NN', 'N', 'A', 'AA'], []);
  
  const getSelectionValue = (selection: string): number => {
    const mapping: Record<string, number> = {
      NN: 1,
      N: 2,
      A: 3,
      AA: 4,
    };
    return mapping[selection] ?? 0;
  };
  
  // 현재 그룹의 키 찾기 (objectKeyIndex를 사용)
  const currentGroupKey = useMemo(() => {
    const keys = Object.keys(inspectionThirdClassification);
    const parentKeys = new Set<string>();
    keys.forEach(key => {
      const parentKey = key.replace(/\d+$/, '');
      parentKeys.add(parentKey);
    });
    const parentKeysArray = Array.from(parentKeys);
    return parentKeysArray[objectKeyIndex] ?? '';
  }, [inspectionThirdClassification, objectKeyIndex]);
  
  // 현재 항목의 2차 분류 정보 찾기
  const handleGetCheckInfo = useMemo(() => {
    if (!currentGroupKey) return null;
    const groupData = inspectionSecondClassification[currentGroupKey as keyof typeof inspectionSecondClassification];
    return groupData?.find((_obj: any) => _obj.id === obj?.id);
  }, [currentGroupKey, inspectionSecondClassification, obj?.id]);
  
  const handleClassification3 = async (newCheck: ToggleValue) => {
    if (!newCheck || !['NN', 'N', 'A', 'AA'].includes(newCheck)) {
      return;
    }
    
    if (!currentGroupKey || !handleGetCheckInfo) return;
    
    const parentValue = currentGroupKey;
    const typeKey = `${parentValue}${getSelectionValue(newCheck as string)}`;
    
    const resultObj: ICmnInspectionProps = {
      ...obj,
      sort: handleGetCheckInfo?.sort ?? -1,
      value: newCheck,
      type: typeKey,
      parent: parentValue || '',
    };
    
    // 기존 타입 찾기 (이미 분류된 경우)
    const existingType = Object.keys(inspectionThirdClassification).find(key => {
      const arr = inspectionThirdClassification[key];
      return arr?.some(item => item.id === obj.id);
    });
    
    await updateInspectionThirdClassificationItem(typeKey, resultObj, existingType);
    
    if (nextCondition.includes(newCheck)) {
      changeDetailProgressInfo(detailProgressInfo + 1);
    }
  };

  useEffect(() => {
    setCheck(value);
    return () => setCheck(null);
  }, [value]);
  return (
    <Box sx={{ width: '100%', p: 0, height: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          justifyContent: 'space-between',
          height: isMobile ? 49 : 72,
          alignItems: 'center',
          px: 2,
          gridTemplateColumns: isMobile ? '1fr 120px' : '250px 1fr 150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, textAlign: 'left' }}>
            <Typography typography="s20w700" noWrap>
              {projectName}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 700,
              maxWidth: '100%',
            }}
            noWrap
          >
            {handleClassificationTitle(classification, objectKeyIndex + 1, total)}
          </Typography>
        </Box>
        <Typography typography="s17w400" sx={{ flex: 1, textAlign: 'right' }}>
          # {detailProgressInfo + 1}
        </Typography>
      </Box>
      <Divider sx={{ borderWidth: 'thick', borderColor: '#EBEBEB' }} />
      <Stack direction="column" sx={{ height: 'calc(100% - 127px - 90px - 48px)', m: 3 }}>
        <Box
          sx={{
            typography: isMobile ? 's18w800' : 's24w800',
            color: 'rgba(192, 192, 192, 1)',
            textDecoration: 'underline',
          }}
        >
          제시되는 문장에 대해 동의하는 정도를 선택해주세요.
        </Box>
        <Typography
          sx={{
            mb: 0,
            minHeight: isMobile ? 180 : 396,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? '20px' : '32px',
            height: '100%',
            fontWeight: 400,
            wordBreak: isMobile ? 'inherit' : 'keep-all',
            lineHeight: isMobile ? '1.1' : '50px',
          }}
        >
          {obj?.content || ''}
        </Typography>
      </Stack>
      <Divider sx={{ borderWidth: 'thick', borderColor: '#EBEBEB' }} />
      <Box
        sx={{
          display: 'flex',
          height: { xs: 100, md: 125 },
          alignItems: 'center',
          justifyContent: 'space-evenly',
          background: 'white',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
        }}
      >
        {buttonInfoList.map(_info => (
          <Fragment key={_info.id}>
            <Stack
              spacing={2}
              direction="column"
              key={_info.id}
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                py: 1,
                '&:hover': {
                  backgroundColor: check !== _info.id ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                },
                '& *': {
                  pointerEvents: 'none',
                },
              }}
              onClick={() => handleClassification3(_info.id as ToggleValue)}
            >
              {!['xs', 'sm', 'md'].includes(_width) && (
                <Box typography="s20w600" sx={{ color: 'rgba(60, 60, 67, 0.6)', textAlign: 'center' }}>
                  {_info.text.replace('\n', ' ')}
                </Box>
              )}
              {['xs', 'sm', 'md'].includes(_width) && (
                <Box
                  typography="s12w400"
                  sx={{ color: 'rgba(60, 60, 67, 0.6)', whiteSpace: 'pre-line', textAlign: 'center' }}
                >
                  {_info.text}
                </Box>
              )}

              <Box
                key={_info.color}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: _info.color,
                  transition: 'transform 0.1s ease-in-out',
                  '&:active': {
                    transform: 'scale(0.9)',
                  },
                }}
              >
                {check === _info.id && (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: '2px solid white',
                      position: 'relative',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </Box>
            </Stack>
            {_info.id !== 'AA' && <Divider orientation="vertical" variant="middle" flexItem sx={{ m: 0 }} />}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

export default DocumentCard3;
